import React from 'react';
import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';

import ValorQueChega from './ValorQueChega';
import { poco } from '../../theme/vidro';

import CoberturaChip from '../../components/CoberturaChip';
import { resumoDoIndice, motivoDoPilarFora } from '../apresentacao';

/**
 * O Índice de Saúde — SEMPRE visível, com o perfil e a cobertura declarados.
 *
 * Metade da base não tem balanço, e os pilares que dependem dele somam 45 dos
 * 100 pontos. Sumir com a nota entrega um vazio justamente na tela que responde
 * "minha empresa está saudável?". Então a nota aparece renormalizada sobre o
 * que o dado permite, com o selo dizendo sobre o que ela foi calculada:
 * qualificar é melhor que esconder.
 *
 * A única situação em que o número não aparece é NENHUM pilar ter entrado —
 * aí a nota do motor é `0` por ser o neutro da soma, e pintar um 0 vermelho
 * acusaria de doente uma empresa que apenas não informou nada.
 */
const CORES = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  default: 'text.disabled'
};

/**
 * A barra de um pilar.
 *
 * A COR DIZ ALGUMA COISA. Antes as cinco barras eram todas do acento: um pilar
 * em 100 e outro em 33 tinham o mesmo azul, e a única diferença era o
 * comprimento — o olho lê comprimento devagar e cor num piscar. Aqui a barra
 * herda a mesma régua do resto do produto: abaixo de 40 é crítico, até 70 é
 * atenção, acima disso é bom.
 *
 * O preenchimento é animado porque uma barra CRESCENDO é a mesma medição sendo
 * desenhada, não um valor falso de passagem — ao contrário de um número que
 * conta de zero, que exibiria medições que ninguém fez. `MotionConfig` do casco
 * desliga isto para quem pediu menos movimento.
 *
 * `role="img"` com `aria-label`: o desenho é a informação, e leitor de tela
 * precisa da mesma frase que o olho recebe.
 */
const BarraDoPilar = ({ nota, nome }) => {
  const valor = Math.max(0, Math.min(100, nota));
  const cor = valor < 40 ? 'error.main' : valor < 70 ? 'warning.main' : 'success.main';

  return (
    <Box
      role="img"
      aria-label={`${nome}: ${nota} de 100`}
      sx={{
        mt: 0.75,
        height: 6,
        borderRadius: 999,
        overflow: 'hidden',
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,34,0.07)')
      }}
    >
      <Box
        component={motion.div}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: valor / 100 }}
        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        sx={{
          height: '100%',
          width: '100%',
          transformOrigin: 'left center',
          borderRadius: 999,
          bgcolor: cor,
          boxShadow: (t) => (t.palette.mode === 'dark' ? '0 0 14px -2px currentColor' : 'none'),
          color: cor
        }}
      />
    </Box>
  );
};

const IndiceDeSaude = ({ leitura }) => {
  const resumo = resumoDoIndice(leitura?.indiceSaude);
  const temBalanco = Boolean(leitura?.temBalanco);

  return (
    <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 4 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
            Índice de Saúde
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 0.5 }}>
            <ValorQueChega>
              <Typography
                className="valor-numerico"
                sx={{
                  fontSize: { xs: '3rem', sm: '3.75rem' },
                  fontWeight: 620,
                  lineHeight: 1,
                  letterSpacing: '-0.035em',
                  color: CORES[resumo.cor] || CORES.default,
                  // A nota é o número mais importante do produto e o único com
                  // halo: sobre vidro escuro, um brilho curto da própria cor faz
                  // o algarismo parecer aceso por dentro em vez de pintado.
                  textShadow: (t) =>
                    t.palette.mode === 'dark' ? '0 0 20px rgba(255,255,255,0.10), 0 0 44px currentColor' : 'none',
                  filter: (t) => (t.palette.mode === 'dark' ? 'saturate(1.05)' : 'none')
                }}
              >
                {resumo.calculado ? resumo.nota : '—'}
              </Typography>
            </ValorQueChega>
            {resumo.calculado && (
              <Typography variant="body2" color="text.secondary">
                de 100
              </Typography>
            )}
            <Typography variant="h5" sx={{ color: CORES[resumo.cor] || CORES.default }}>
              {resumo.rotulo}
            </Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, maxWidth: '60ch' }}>
            {resumo.selo}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <CoberturaChip cobertura={leitura?.cobertura} />
        </Box>
      </Stack>

      {(resumo.dentro.length > 0 || resumo.fora.length > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          {/* OS PILARES EM DUAS COLUNAS NA TELA LARGA.
              Numa área de trabalho de 1600px, uma barra por linha estica o
              indicador por toda a largura e joga o rótulo a meia tela do
              número. Em duas colunas a barra volta a ter comprimento de barra e
              o cartão cabe numa dobra. */}
          <Box
            sx={(t) => ({
              ...poco(t.palette.mode === 'dark'),
              p: { xs: 1.75, sm: 2.25 },
              display: 'grid',
              gap: { xs: 1.75, md: 2.25 },
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              columnGap: { lg: 5 }
            })}
          >
            {resumo.dentro.map((pilar) => (
              <Box key={pilar.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                  <Typography variant="body2">{pilar.nome}</Typography>
                  <Typography variant="body2" className="valor-numerico" sx={{ fontWeight: 600 }}>
                    {pilar.nota}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {` · peso ${pilar.peso}`}
                    </Typography>
                  </Typography>
                </Stack>
                <BarraDoPilar nota={pilar.nota} nome={pilar.nome} />
              </Box>
            ))}

          </Box>

          <Stack spacing={1.5} sx={{ mt: resumo.fora.length ? 2 : 0 }}>
            {resumo.fora.map((pilar) => (
              <Stack
                key={pilar.id}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={2}
              >
                <Typography variant="body2" color="text.secondary">
                  {pilar.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {motivoDoPilarFora(pilar, temBalanco)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Card>
  );
};

export default IndiceDeSaude;
